import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { useBranding } from "../lib/branding";
import { reviewPath, withDemoFlag } from "../lib/links";
import { trackClick, wealthSiteUrl, AUGUSTA_URL } from "../lib/crossSell";

const links = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/booking", label: "Book a Call" },
];

export default function Header() {
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const { branding } = useBranding();
    const orgName = branding?.organization_name || "Your Annuity Review";
    const tagline = branding?.tagline || "yourannuityreview.com";

    return (
        <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-slate-200" data-testid="site-header">
            <div className="max-w-7xl mx-auto px-6 sm:px-8 py-4 flex items-center justify-between">
                <Link to={withDemoFlag("/")} className="flex items-center gap-3 group" data-testid="logo-link">
                    {branding?.logo_url ? (
                        <img src={branding.logo_url} alt={orgName} className="w-10 h-10 rounded-md object-cover bg-white border border-slate-200" />
                    ) : (
                        <div className="w-10 h-10 rounded-md bg-navy-900 flex items-center justify-center text-white">
                            <ShieldCheck className="w-5 h-5" />
                        </div>
                    )}
                    <div className="leading-tight">
                        <div className="font-serif text-xl text-navy-900 font-semibold tracking-tight" data-testid="brand-name">{orgName}</div>
                        <div className="uppercase tracking-[0.2em] text-[10px] font-semibold text-slate-700" data-testid="brand-tagline">{tagline}</div>
                    </div>
                </Link>

                <nav className="hidden md:flex items-center gap-8" data-testid="primary-nav">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={withDemoFlag(l.to)}
                            data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `text-base font-medium transition-colors ${isActive ? "text-navy-900" : "text-slate-600 hover:text-navy-900"}`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                    {/* Secondary cross-sell — subtle, never competes with the primary CTA */}
                    <a
                        href={wealthSiteUrl()}
                        onClick={() => trackClick("toolkit", { source_page: "annuity_header" })}
                        className="hidden lg:inline text-[13px] font-medium text-slate-700 hover:text-navy-900 transition-colors"
                        data-testid="nav-toolkit"
                    >
                        Get the Toolkit
                    </a>
                    <a
                        href={AUGUSTA_URL}
                        target="_blank"
                        rel="noopener noreferrer sponsored"
                        onClick={() => trackClick("augusta", { source_page: "annuity_header" })}
                        className="hidden lg:inline text-[13px] font-medium text-slate-700 hover:text-navy-900 transition-colors"
                        data-testid="nav-augusta"
                    >
                        Gold and Silver IRA
                    </a>
                </nav>

                <div className="hidden md:block">
                    <Button
                        onClick={() => navigate(reviewPath())}
                        data-testid="header-cta-review"
                        className="bg-navy-900 hover:bg-navy-800 text-white px-6 py-5 rounded-md font-medium text-base"
                    >
                        Get My Free Review
                    </Button>
                </div>

                <button
                    className="md:hidden p-2 rounded-md border border-slate-200"
                    onClick={() => setOpen(!open)}
                    data-testid="mobile-menu-toggle"
                    aria-label="Toggle menu"
                >
                    {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </div>

            {open && (
                <div className="md:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
                    <div className="px-6 py-4 flex flex-col gap-4">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={withDemoFlag(l.to)}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={({ isActive }) =>
                                    `text-lg font-medium ${isActive ? "text-navy-900" : "text-slate-700"}`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <Button
                            onClick={() => { setOpen(false); navigate(reviewPath()); }}
                            data-testid="mobile-cta-review"
                            className="bg-navy-900 hover:bg-navy-800 text-white py-5 rounded-md"
                        >
                            Get My Free Review
                        </Button>
                    </div>
                </div>
            )}
        </header>
    );
}
